import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  InputSignal,
  OnInit,
  signal,
  Signal,
  untracked,
  viewChild,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { ExtendedEditorComponent } from '@shared/modules/rich-editor/components/extended-editor/extended-editor.component';
import { DataMode } from '@shared/modules/rich-editor/helpers/enums/data-mode.enum';
import { UnsaveChangesEvaluation } from '@shared/modules/rich-editor/interfaces/unsave-changes-evaluation';
import { RichEditorService } from '@shared/modules/rich-editor/services/rich-editor.service';
import { CurrentUserService } from '@shared/services';
import { SidebarItemListService } from '@shared/services/sidebar-item.service';
import moment from 'moment';
import { InvestmentJurisdictionShort } from 'src/app/modules/admin/models/investment-jurisdiction-short';
import { TaxSummaryDetail } from '../../models/tax-summary-detail';
import { TaxSummaryService } from '../../services/tax-summary.service';

@Component({
  standalone: false,
  selector: 'gstar-tax-summary-editor',
  templateUrl: './tax-summary-editor.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TaxSummaryEditorComponent
  implements OnInit, UnsaveChangesEvaluation
{
  private readonly router = inject(Router);
  private readonly activeRichEditor = inject(RichEditorService);
  private readonly taxSummaryService = inject(TaxSummaryService);
  private readonly sidebarService = inject(SidebarItemListService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly currentUserService = inject(CurrentUserService);

  countryId: InputSignal<number> = input.required();
  taxSummaryId: InputSignal<string> = input.required();

  title: WritableSignal<string> = signal('');
  countryName: WritableSignal<string> = signal('');
  mode: WritableSignal<DataMode> = signal(DataMode.edition);
  content: WritableSignal<string> = signal('');
  taxSummaryDetails: WritableSignal<TaxSummaryDetail | undefined> =
    signal(undefined);
  reviewDateValue: WritableSignal<Date[]> = signal([]);
  reviewDateForm: FormGroup = this.formBuilder.group({ reviewDate: [[]] });
  canEditCountry: Signal<boolean> = computed(() =>
    this.currentUserService.canEditCountry(this.countryId())
  );

  editor: Signal<ExtendedEditorComponent | undefined> = viewChild(
    ExtendedEditorComponent
  );

  private editorInitialized = false;

  constructor() {
    this.sidebarService.selectedCountry$.subscribe(
      (country: InvestmentJurisdictionShort | null | undefined) => {
        if (country) {
          this.countryName.set(country.name);
        }
      }
    );

    this.activeRichEditor.richEditor$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        queueMicrotask(() => this.updateHasNotChanges());
      });

    effect(() => {
      const isPermissionsReady =
        this.currentUserService.countryAdminPermissionsReady();
      const countryId = this.countryId();
      if (
        !isPermissionsReady ||
        countryId === null ||
        countryId === undefined ||
        this.editorInitialized
      ) {
        return;
      }

      this.editorInitialized = true;
      untracked(() => {
        if (!this.currentUserService.canEditCountry(countryId)) {
          this.goToMain();
          return;
        }
        this.loadTaxSummary();
      });
    });
  }

  isInvalid(): boolean {
    return this.activeRichEditor.isEmpty;
  }

  ngOnInit(): void {
    this.reviewDateForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.updateHasNotChanges());
  }

  cancel(): void {
    this.goToMain();
  }

  save(html: string): void {
    if (!this.canEditCountry()) {
      return;
    }

    this.taxSummaryService
      .updateTaxSummary(
        this.taxSummaryDetails()!,
        html,
        this.selectedReviewDate()
      )
      .subscribe(() => {
        this.editor()?.hasNotChanges.set(true);
        this.goToMain();
      });
  }

  hasUnsaveChanges(): boolean {
    return !this.editor()?.hasNotChanges();
  }

  saveUnsaveChanges(html: string): void {
    if (!this.canEditCountry()) {
      return;
    }

    this.taxSummaryService
      .updateTaxSummary(
        this.taxSummaryDetails()!,
        html,
        this.selectedReviewDate()
      )
      .subscribe(() => {
        this.editor()?.hasNotChanges.set(true);
      });
  }

  private loadTaxSummary(): void {
    if (this.taxSummaryId()) {
      this.taxSummaryService
        .taxSummaryById(parseInt(this.taxSummaryId(), 10))
        .subscribe((taxSummaryDetail: TaxSummaryDetail | undefined) => {
          if (taxSummaryDetail) {
            this.title.set(
              `${taxSummaryDetail.header.name}-${taxSummaryDetail.taxSummaryType.name}`
            );
            this.content.set(taxSummaryDetail?.description ?? '');
            this.taxSummaryDetails.set(taxSummaryDetail);
            this.setReviewDate(taxSummaryDetail.reviewDate);
          }
        });
    } else {
      this.mode.set(DataMode.edition);
    }
  }

  private setReviewDate(reviewDate?: Date): void {
    const normalized = this.normalizeReviewDate(reviewDate);
    const dates = normalized ? [normalized, normalized] : [];
    this.reviewDateValue.set(dates);
    this.reviewDateForm.patchValue({ reviewDate: dates }, { emitEvent: false });
    this.reviewDateForm.markAsPristine();
  }

  private selectedReviewDate(): Date | undefined {
    const value = this.reviewDateForm.get('reviewDate')?.value;
    const date = Array.isArray(value) ? value[0] : value;
    return this.normalizeReviewDate(date ?? undefined);
  }

  private normalizeReviewDate(reviewDate?: Date): Date | undefined {
    if (!reviewDate) return undefined;
    const parsed = moment(reviewDate);
    return parsed.isValid() && parsed.isSameOrAfter('2000-01-01')
      ? parsed.toDate()
      : undefined;
  }

  private updateHasNotChanges(): void {
    if (!this.editor()) {
      return;
    }
    const hasDateChanges = this.reviewDateForm.dirty;
    const hasEditorChanges = this.activeRichEditor.hasFileUnsavedChanges;
    this.editor()?.hasNotChanges.set(!hasDateChanges && !hasEditorChanges);
  }

  private goToMain(): void {
    this.router.navigate([
      `/country/${this.countryId()}/tax-rates-summaries/${this.taxSummaryDetails()?.header.taxSummaryTypeId ?? ''}`,
    ]);
  }
}
